import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Banner, Button, Card, Field, Input } from "@magarac/ui";
import { HelpIcon } from "../components/HelpIcon";
import { authClient, GOOGLE_ENABLED } from "../lib/auth-client";
import { OFFLINE_MESSAGE } from "../lib/errors";
import { safeReturnPath } from "../lib/return-path";

interface LoginSearch {
  redirect?: string;
  error?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: LoginPage,
});

/** Better Auth sends people back with ?error= when an email link or Google sign-in fails. */
function linkErrorMessage(code: string | undefined): string | null {
  if (!code) return null;
  if (/expired|invalid_token|INVALID_TOKEN/i.test(code)) {
    return "That sign-in link has expired or was already used. Send yourself a new one.";
  }
  if (/access_denied/i.test(code)) return "Google sign-in was cancelled. Try again when ready.";
  return "We could not sign you in with that link. Please try again.";
}

function signInFailure(err: { status?: number }): string {
  if (err.status === 429) return "Too many tries. Wait a minute, then try again.";
  if (!err.status) return OFFLINE_MESSAGE;
  return "That email and password did not match. Please try again.";
}

/** ACC-1: email + password, magic link, Google (when configured). Screen key: login. */
function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const returnTo = safeReturnPath(search.redirect);
  // Email links and Google come back to the app (not the API host) and land where the person
  // was headed; failures come back here with ?error=.
  const origin = window.location.origin;
  const callbackURL = `${origin}${returnTo}`;
  const errorCallbackURL = `${origin}/login`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() => linkErrorMessage(search.error));
  const [notice, setNotice] = useState<string | null>(null);
  const [step, setStep] = useState<"password" | "code">("password");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await authClient.signIn
      .email({ email, password })
      .catch(() => ({ data: null, error: { status: 0 } }));
    setBusy(false);
    if (err) {
      setError(signInFailure(err));
      return;
    }
    // ACC-6: two-step sign-in is on for this account; ask for the code before going in.
    if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) {
      setStep("code");
      return;
    }
    await navigate({ href: returnTo });
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await (
      useBackup
        ? authClient.twoFactor.verifyBackupCode({ code: code.trim(), trustDevice })
        : authClient.twoFactor.verifyTotp({ code: code.trim(), trustDevice })
    ).catch(() => ({ error: { status: 0 } }));
    setBusy(false);
    if (err) {
      // Better Auth answers a wrong code and an expired sign-in both with 401; tell them apart
      // by its error code so a typo does not throw the person back to the password.
      const kind = "code" in err ? String(err.code) : "";
      const expired = kind === "INVALID_TWO_FACTOR_COOKIE";
      setError(
        !err.status
          ? OFFLINE_MESSAGE
          : err.status === 429 || kind === "ACCOUNT_TEMPORARILY_LOCKED"
            ? "Too many tries. Wait a few minutes, then try again."
            : expired
              ? "That sign-in expired. Sign in with your password again."
              : useBackup
                ? "That backup code did not work. Each one works only once."
                : "That code did not work. Codes change every 30 seconds; type the newest one.",
      );
      if (expired) setStep("password");
      return;
    }
    await navigate({ href: returnTo });
  };

  if (step === "code") {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
        <h1 className="mb-4 text-2xl font-semibold">Enter your code</h1>
        <Card>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <form onSubmit={(e) => void verify(e)} noValidate>
            <Field
              id="code"
              label={useBackup ? "Backup code" : "6-digit code from your authenticator app"}
            >
              <Input
                id="code"
                inputMode={useBackup ? "text" : "numeric"}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
            </Field>
            <label className="mb-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              Do not ask again on this device for 30 days
            </label>
            <Button type="submit" busy={busy} className="w-full">
              Sign in
            </Button>
          </form>
          <button
            type="button"
            className="mt-4 text-sm text-brand-600 underline"
            onClick={() => {
              setUseBackup(!useBackup);
              setCode("");
              setError(null);
            }}
          >
            {useBackup ? "Use my authenticator app instead" : "Lost your phone? Use a backup code"}
          </button>
        </Card>
      </main>
    );
  }

  const sendLink = async () => {
    if (!email) {
      setError("Type your email first, then choose the email link.");
      return;
    }
    setBusy(true);
    setError(null);
    const { error: err } = await authClient.signIn
      .magicLink({ email, callbackURL, errorCallbackURL })
      .catch(() => ({ error: { status: 0 } }));
    setBusy(false);
    if (err) {
      setError(
        err.status === 429
          ? "Too many links requested. Wait a minute, then try again."
          : !err.status
            ? OFFLINE_MESSAGE
            : "We could not send the link. Please try again in a moment.",
      );
      return;
    }
    setNotice("Check your email for a sign-in link. It works once and expires in a few minutes.");
  };

  const google = async () => {
    await authClient.signIn.social({ provider: "google", callbackURL, errorCallbackURL });
  };

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Sign in</h1>
        <HelpIcon screen="login" />
      </div>
      <Card>
        {notice ? <Banner tone="ok">{notice}</Banner> : null}
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <form onSubmit={(e) => void signIn(e)} noValidate>
          <Field id="email" label="Email">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field id="password" label="Password">
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-2">
            <Button type="submit" busy={busy}>
              Sign in
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void sendLink()}
              disabled={busy}
            >
              Email me a sign-in link
            </Button>
            {GOOGLE_ENABLED ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => void google()}
                disabled={busy}
              >
                Continue with Google
              </Button>
            ) : null}
          </div>
        </form>
        <p className="mt-4 text-center text-sm">
          New to J360?{" "}
          <Link to="/signup" className="text-brand-700 underline">
            Create an account
          </Link>
        </p>
      </Card>
      <p className="mt-4 text-center text-sm text-muted">
        Were you invited? Open the link in your invitation email to get started.
      </p>
    </main>
  );
}
